<template>
  <div class="rm-detail" v-if="todoList && todo && todo.listId != null">
    <!-- Reminders 顶栏：返回列表名 + 更多 -->
    <header class="rm-nav">
      <button type="button" class="rm-nav-back" @click="goBack">
        <i class="bi-chevron-left" aria-hidden="true"></i>
        <span class="rm-nav-back-label">{{ backLabel }}</span>
      </button>
      <h1 class="rm-nav-title">{{ $t("todoDetails.details") }}</h1>
      <div class="dropdown rm-nav-more">
        <button
          type="button"
          class="rm-nav-icon"
          id="rmTodoMoreMenu"
          data-bs-toggle="dropdown"
          :aria-label="$t('todoDetails.actions')"
        >
          <i class="bi-three-dots" aria-hidden="true"></i>
        </button>
        <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="rmTodoMoreMenu">
          <li>
            <button class="dropdown-item" type="button" @click="copyTodo">
              <i class="bi-clipboard"></i>
              <span>{{ $t("donate.copy") }}</span>
            </button>
          </li>
          <li><hr class="dropdown-divider" /></li>
          <li>
            <button class="dropdown-item text-danger" type="button" @click="onRemove">
              <i class="bi-trash"></i>
              <span>{{ $t("ui.remove") }}</span>
            </button>
          </li>
          <li v-if="todo.repeatingEvent">
            <button class="dropdown-item text-danger" type="button" @click="removeAll">
              <i class="bi-trash"></i>
              <span>{{ $t("ui.removeAll") }}</span>
            </button>
          </li>
        </ul>
      </div>
    </header>

    <div class="rm-scroll">
      <!-- 标题 + 注解（Reminders 主区） -->
      <section class="rm-hero">
        <button
          type="button"
          class="rm-check"
          :aria-pressed="!!todo.checked"
          @click="toggleTodoChecked"
        >
          <span
            v-if="todo.color != 'none'"
            class="cicle-icon"
            :style="'color: ' + todo.color"
            :class="{
              'bi-check-circle-fill': todo.checked,
              'bi-circle-fill': !todo.checked,
            }"
          ></span>
          <span
            v-else
            class="cicle-icon"
            :class="{
              'bi-check-circle': todo.checked,
              'bi-circle': !todo.checked,
            }"
          ></span>
        </button>
        <div class="rm-hero-main">
          <textarea
            class="rm-title-input"
            rows="1"
            v-model="todo.text"
            ref="titleInput"
            :placeholder="$t('todoDetails.taskTitle')"
            :class="{ 'is-checked': todo.checked }"
            @input="resizeTitleInput"
            @blur="doneEditTitle()"
            @keydown.enter.exact.prevent="onTitleEnter"
            @focus="onTitleFocus()"
          ></textarea>
          <description-text-area
            :todoDesc="todo.desc"
            @updated-description="changeDescription"
          ></description-text-area>
        </div>
      </section>

      <!-- 日期 / 列表 / 时间 / 提醒 / 重复 / 颜色 -->
      <section class="rm-group">
        <!-- 仅日历：改日期 -->
        <div
          v-if="showCal && !showCL && showingCalendar"
          class="rm-row rm-row--date"
          role="button"
          tabindex="0"
          @click="showCalendar"
          @keydown.enter.prevent="showCalendar"
        >
          <span class="rm-row-icon bi-calendar-event" aria-hidden="true"></span>
          <span class="rm-row-label">{{ $t("todoDetails.date") }}</span>
          <span class="rm-row-value">{{ listTitle }}</span>
          <i class="bi-chevron-right rm-row-chevron" aria-hidden="true"></i>
        </div>

        <!-- 仅自定义列表：选列表 -->
        <template v-else-if="showCL && !showCal">
          <button
            type="button"
            class="rm-row"
            :class="{ 'is-open': openPanel === 'place' }"
            @click="togglePlacePanel"
          >
            <span class="rm-row-icon bi-list-ul" aria-hidden="true"></span>
            <span class="rm-row-label">{{ $t("settings.customLists") }}</span>
            <span class="rm-row-value">{{ pickedCListName }}</span>
            <i class="bi-chevron-right rm-row-chevron" aria-hidden="true"></i>
          </button>
          <div v-if="openPanel === 'place'" class="rm-panel rm-place-panel" @click.stop>
            <button
              v-for="option in cListOptions"
              :key="option.listId"
              type="button"
              class="rm-place-option"
              :class="{ selected: option.listId == pickedCList }"
              @click="selectCList(option)"
            >
              <span class="rm-place-name">{{ option.listName }}</span>
              <i v-if="option.listId == pickedCList" class="bi-check2 rm-place-check" aria-hidden="true"></i>
            </button>
            <p v-if="!cListOptions.length" class="rm-place-empty">{{ $t("todoDetails.none") }}</p>
          </div>
        </template>

        <!-- 日历 + 自定义列表：统一「放到哪」 -->
        <template v-else-if="showCL && showCal">
          <button
            type="button"
            class="rm-row"
            :class="{ 'is-open': openPanel === 'place' }"
            @click="togglePlacePanel"
          >
            <span
              class="rm-row-icon"
              :class="showingCalendar ? 'bi-calendar-event' : 'bi-list-ul'"
              aria-hidden="true"
            ></span>
            <span class="rm-row-label">{{ $t("todoDetails.placeIn") }}</span>
            <span class="rm-row-value">{{ placeLabel }}</span>
            <i class="bi-chevron-right rm-row-chevron" aria-hidden="true"></i>
          </button>
          <div v-if="openPanel === 'place'" class="rm-panel rm-place-panel" @click.stop>
            <div class="rm-place-section">{{ $t("settings.calendar") }}</div>
            <button
              type="button"
              class="rm-place-option"
              :class="{ selected: showingCalendar }"
              @click="selectCalendarPlace"
            >
              <i class="bi-calendar-event rm-place-leading" aria-hidden="true"></i>
              <span class="rm-place-text">
                <span class="rm-place-name">{{
                  showingCalendar ? listTitle : $t("todoDetails.pickDate")
                }}</span>
              </span>
              <i v-if="showingCalendar" class="bi-check2 rm-place-check" aria-hidden="true"></i>
            </button>

            <div v-if="cListOptions.length" class="rm-place-section">{{ $t("settings.customLists") }}</div>
            <button
              v-for="option in cListOptions"
              :key="option.listId"
              type="button"
              class="rm-place-option"
              :class="{ selected: !showingCalendar && option.listId == pickedCList }"
              @click="selectCList(option)"
            >
              <i class="bi-list-ul rm-place-leading" aria-hidden="true"></i>
              <span class="rm-place-name">{{ option.listName }}</span>
              <i
                v-if="!showingCalendar && option.listId == pickedCList"
                class="bi-check2 rm-place-check"
                aria-hidden="true"
              ></i>
            </button>
            <p v-if="!cListOptions.length" class="rm-place-empty">{{ $t("todoDetails.none") }}</p>
          </div>
        </template>

        <!-- 时间：行内滚轮 -->
        <button
          type="button"
          class="rm-row"
          :class="{ 'is-open': openPanel === 'time' }"
          @click="togglePanel('time')"
        >
          <span class="rm-row-icon bi-clock" aria-hidden="true"></span>
          <span class="rm-row-label">{{ $t("todoDetails.time") }}</span>
          <span class="rm-row-value" :class="{ 'is-placeholder': !todo.time }">{{ timeLabel }}</span>
          <i class="bi-chevron-right rm-row-chevron" aria-hidden="true"></i>
        </button>
        <div v-if="openPanel === 'time'" class="rm-panel" @click.stop>
          <time-picker :time="todo.time" inline @time-selected="onTimeSelected"></time-picker>
        </div>

        <!-- 提醒（需先有时间） -->
        <button
          type="button"
          class="rm-row"
          :disabled="!todo.time"
          @click="changeAlarm"
        >
          <span class="rm-row-icon" :class="todo.alarm ? 'bi-bell-fill' : 'bi-bell'" aria-hidden="true"></span>
          <span class="rm-row-label">{{ $t("todoDetails.alarm") }}</span>
          <span class="rm-row-value">{{ todo.alarm ? $t("todoDetails.alarmOn") : $t("todoDetails.alarmOff") }}</span>
          <span
            class="rm-switch"
            :class="{ on: todo.alarm, disabled: !todo.time }"
            aria-hidden="true"
          ></span>
        </button>

        <!-- 重复（仅日历任务） -->
        <button
          v-if="showingCalendar"
          type="button"
          class="rm-row"
          :class="{ 'is-open': openPanel === 'repeat' }"
          @click="togglePanel('repeat')"
        >
          <span class="rm-row-icon bi-arrow-repeat" aria-hidden="true"></span>
          <span class="rm-row-label">{{ $t("ui.recurringTasks") }}</span>
          <span class="rm-row-value">{{ repeatLabel }}</span>
          <i class="bi-chevron-right rm-row-chevron" aria-hidden="true"></i>
        </button>
        <div v-if="showingCalendar && openPanel === 'repeat'" class="rm-panel" @click.stop>
          <repeating-event
            :repeatingEvent="todo.repeatingEvent"
            :todo="todo"
            inline
            @repeatingEventSelected="onRepeatingSelected"
            @close="openPanel = null"
          ></repeating-event>
        </div>

        <!-- 颜色 -->
        <button type="button" class="rm-row" :class="{ 'is-open': openPanel === 'color' }" @click="togglePanel('color')">
          <span class="rm-row-icon bi-circle-fill" :style="colorIconStyle" aria-hidden="true"></span>
          <span class="rm-row-label">{{ $t("todoDetails.color") }}</span>
          <span class="rm-row-value">{{ colorLabel }}</span>
          <i class="bi-chevron-right rm-row-chevron" aria-hidden="true"></i>
        </button>
        <div v-if="openPanel === 'color'" class="rm-panel" @click.stop>
          <color-picker :color="todo.color" inline @color-selected="onColorSelected"></color-picker>
        </div>
      </section>

      <!-- 子任务 -->
      <div class="rm-section-label">{{ $t("todoDetails.subtasks") }}</div>
      <section class="rm-group">
        <ul class="rm-subtasks">
          <li v-for="(subTask, index) in todo.subTaskList" :key="index" class="rm-subtask">
            <div v-show="!subTask.editing" class="rm-subtask-row" :class="{ checked: subTask.checked }">
              <button
                type="button"
                class="rm-subtask-check"
                :aria-pressed="!!subTask.checked"
                :aria-label="subTask.checked ? 'Uncheck' : 'Check'"
                @click="onSubtaskCheck(index)"
              >
                <span
                  class="cicle-icon"
                  :class="{
                    'bi-check-circle': subTask.checked,
                    'bi-circle': !subTask.checked,
                  }"
                ></span>
              </button>
              <button type="button" class="rm-subtask-text" @click="changeSubTaskClickhandler(index)">
                <span v-html="linkifyText(subTask.text)"></span>
              </button>
              <button
                type="button"
                class="rm-subtask-del"
                :aria-label="$t('ui.remove')"
                @click="removeSubTask(index)"
              >
                <i class="bi-x-circle" aria-hidden="true"></i>
              </button>
            </div>
            <input
              v-show="subTask.editing"
              v-model="subTask.text"
              @blur="doneEditSubTask(index)"
              @keyup.enter="doneEditSubTask(index)"
              :ref="'subTaskEdit' + index"
              class="rm-subtask-edit"
            />
          </li>
          <li class="rm-subtask rm-subtask--new">
            <i class="bi-plus-circle rm-subtask-add-ico" aria-hidden="true"></i>
            <input
              type="text"
              id="mobile-new-sub-task"
              class="rm-subtask-new-input"
              :placeholder="$t('todoDetails.addSubTask')"
              autocomplete="off"
              @blur="addSubTask()"
              @keyup.enter="addSubTask()"
              v-model="newSubTask.text"
              ref="newSubTask"
            />
          </li>
        </ul>
      </section>

      <todo-activity-section v-if="todo && todo.id" :todo-id="todo.id" />

      <!-- 底部操作 -->
      <section class="rm-group rm-group--actions">
        <button type="button" class="rm-row rm-row--action" @click="copyTodo">
          <span class="rm-row-label">{{ $t("donate.copy") }}</span>
        </button>
      </section>
      <section class="rm-group rm-group--actions">
        <button type="button" class="rm-row rm-row--danger" @click="onRemove">
          <span class="rm-row-label">{{ $t("ui.remove") }}</span>
        </button>
        <button
          v-if="todo.repeatingEvent"
          type="button"
          class="rm-row rm-row--danger"
          @click="removeAll"
        >
          <span class="rm-row-label">{{ $t("ui.removeAll") }}</span>
        </button>
      </section>

      <div class="rm-scroll-pad"></div>
    </div>

    <!-- 日期选择器挂在滚动区外，fixed 居中，避免被裁切 / 挤乱行布局 -->
    <div v-if="showingCalendar" class="rm-date-host" @click.stop>
      <datepicker
        id="mobile-todo-date-picker-input"
        v-model="pickedDate"
        :locale="language"
        :input-format="'dd/MM/yyyy'"
        :weekStartsOn="weekStartOnMonday"
      />
    </div>

    <comfirm-modal
      :id="'removeReModalMobileTodoDetail'"
      :title="$t('ui.removeRepeatingTask')"
      :text="$t('ui.repeatingTaskRemoveConfirm')"
      :ico="'bi-x-circle'"
      :okText="$t('ui.remove')"
      @on-ok="removeAllComfirmed"
      @on-cancel="onRemoveAllCanceled"
    ></comfirm-modal>
  </div>
</template>

<script>
import Datepicker from "vue3-datepicker";
import moment from "moment";
import colorPicker from "../views/toDoModal/colorPicker";
import timePicker from "../views/toDoModal/timePicker";
import repeatingEvent from "../views/toDoModal/repeatingEvent";
import descriptionTextArea from "../views/toDoModal/descriptionTextArea.vue";
import comfirmModal from "../components/comfirmModal.vue";
import todoActivitySection from "../components/todoActivitySection.vue";
import todoDetailMixin from "../views/toDoModal/todoDetailMixin";
const { closeTodoDetail, clearTodoDetail } = require("./mobileNav.js");

export default {
  name: "MobileTodoDetailPage",
  mixins: [todoDetailMixin],
  components: {
    Datepicker,
    colorPicker,
    timePicker,
    repeatingEvent,
    descriptionTextArea,
    comfirmModal,
    todoActivitySection,
  },
  props: {
    listId: { type: String, required: true },
    todoIndex: { type: Number, required: true },
  },
  data() {
    return {
      openPanel: null, // 'time' | 'repeat' | 'color' | 'place'
    };
  },
  watch: {
    listId: { immediate: true, handler: "reloadFromProps" },
    todoIndex: { immediate: true, handler: "reloadFromProps" },
    pickedDate(newVal) {
      if (this.loadingView) return;
      var newListId = moment(newVal).format("YYYYMMDD");
      if (this.todo && newListId != this.todo.listId) {
        this.moveToTodoList(newListId);
      }
    },
    pickedCList(newVal) {
      if (this.loadingView) return;
      this.moveToTodoList(newVal);
    },
  },
  computed: {
    backLabel() {
      if (this.showingCalendar) return this.$t("settings.calendar");
      return this.pickedCListName || this.$t("settings.customLists");
    },
    placeLabel() {
      if (this.showingCalendar) return this.listTitle;
      return this.pickedCListName || this.$t("todoDetails.none");
    },
    timeLabel() {
      if (!this.todo || !this.todo.time) return this.$t("todoDetails.none");
      return moment(this.todo.time, "HH:mm").format("HH:mm");
    },
    repeatLabel() {
      if (!this.todo || !this.todo.repeatingEvent) {
        return this.$t("todoDetails.noRepeat");
      }
      const re = this.$store.getters.repeatingEventList[this.todo.repeatingEvent];
      if (!re) return this.$t("ui.recurringTasks");
      const typeMap = {
        0: "todoDetails.yearly",
        1: "todoDetails.monthly",
        2: "todoDetails.weekly",
        3: "todoDetails.daily",
        4: "todoDetails.weekdays",
        5: "todoDetails.customWeekdays",
        6: "todoDetails.daysOfMonth",
      };
      const key = typeMap[re.type];
      return key ? this.$t(key) : this.$t("ui.recurringTasks");
    },
    colorLabel() {
      if (!this.todo || !this.todo.color || this.todo.color === "none") {
        return this.$t("todoDetails.none");
      }
      return "";
    },
    colorIconStyle() {
      if (!this.todo || !this.todo.color || this.todo.color === "none") {
        return { color: "rgba(60,60,67,0.3)" };
      }
      return { color: this.todo.color };
    },
  },
  methods: {
    getRemoveConfirmModalId() {
      return "removeReModalMobileTodoDetail";
    },
    showCalendar() {
      const el = document.getElementById("mobile-todo-date-picker-input");
      if (!el) return;
      // vue3-datepicker 靠 click 打开弹层；仅 focus 在移动端常无效
      el.click();
      el.focus();
    },
    reloadFromProps() {
      this.openPanel = null;
      const list = this.$store.getters.todoLists[this.listId];
      if (!list || !list[this.todoIndex]) {
        clearTodoDetail();
        return;
      }
      this.applySelectedTodo({
        toDo: list[this.todoIndex],
        index: this.todoIndex,
      });
      this.$nextTick(() => this.resizeTitleInput());
    },
    resizeTitleInput() {
      const el = this.$refs.titleInput;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = `${Math.max(el.scrollHeight, 29)}px`;
    },
    goBack() {
      closeTodoDetail();
    },
    onTodoRemoved() {
      this.goBack();
    },
    onRemove() {
      this.removeTodo();
    },
    onRemoveAllCanceled() {},
    onSubtaskCheck(index) {
      const item = this.todo.subTaskList[index];
      if (!item) return;
      item.checked = !item.checked;
      this.changeSubTask(index);
    },
    togglePanel(name) {
      this.openPanel = this.openPanel === name ? null : name;
    },
    togglePlacePanel() {
      if (this.openPanel !== "place") {
        this.getCListOptions();
      }
      this.togglePanel("place");
    },
    selectCalendarPlace() {
      this.openPanel = null;
      if (this.showingCalendar) {
        this.$nextTick(() => this.showCalendar());
        return;
      }
      this.loadingView = true;
      this.showingCalendar = true;
      const todayId = moment().format("YYYYMMDD");
      this.pickedDate = moment().toDate();
      this.pickedCList = "";
      this.pickedCListName = "";
      this.$nextTick(() => {
        this.loadingView = false;
        this.moveToTodoList(todayId);
        this.$nextTick(() => this.showCalendar());
      });
    },
    selectCList(option) {
      if (!option) return;
      this.openPanel = null;
      this.pickedCListName = option.listName;
      this.showingCalendar = false;
      this.pickedDate = null;
      if (this.todo && this.todo.listId === option.listId) {
        this.pickedCList = option.listId;
        return;
      }
      this.loadingView = true;
      this.pickedCList = option.listId;
      this.$nextTick(() => {
        this.loadingView = false;
        this.moveToTodoList(option.listId);
      });
    },
    onTimeSelected(time) {
      this.changeTime(time);
      this.openPanel = null;
    },
    onColorSelected(color) {
      this.changeColor(color);
      this.openPanel = null;
    },
    onRepeatingSelected(repeatingEvent) {
      // Link seed only — do NOT close the panel here. closing via v-if destroys
      // <repeating-event> mid-done() and aborts materialize of later COUNT days
      // (e.g. Mon daily×10 → days in the next week never get written).
      this.changeRepeatingEvent(repeatingEvent);
    },
  },
};
</script>

<style scoped lang="scss">
.rm-detail {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  background: #f2f2f7;
  color: #000;
  overscroll-behavior: contain;
}

.dark-theme .rm-detail {
  background: #000;
  color: #f2f2f7;
}

.rm-nav {
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  column-gap: 4px;
  min-height: 44px;
  padding: 6px 8px;
  padding-top: max(6px, env(safe-area-inset-top));
  background: rgba(242, 242, 247, 0.92);
  backdrop-filter: saturate(180%) blur(16px);
  -webkit-backdrop-filter: saturate(180%) blur(16px);
  border-bottom: 0.5px solid rgba(60, 60, 67, 0.18);
}

.dark-theme .rm-nav {
  background: rgba(0, 0, 0, 0.86);
  border-bottom-color: rgba(84, 84, 88, 0.65);
}

.rm-nav-back {
  justify-self: start;
  display: inline-flex;
  align-items: center;
  gap: 0;
  border: none;
  background: transparent;
  color: #007aff;
  font-size: 17px;
  padding: 6px 4px;
  min-width: 0;
  max-width: 100%;

  i {
    font-size: 1.35rem;
    margin-right: -2px;
  }
}

.rm-nav-back-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 400;
}

.rm-nav-title {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  text-align: center;
  justify-self: center;
}

.rm-nav-more {
  justify-self: end;
}

.rm-nav-icon {
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  color: #007aff;
  font-size: 1.25rem;
  border-radius: 10px;
}

.rm-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 12px 16px 0;
}

.rm-hero {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #fff;
  border-radius: 12px;
  padding: 14px 12px 12px;
  margin-bottom: 18px;
}

.dark-theme .rm-hero {
  background: #1c1c1e;
}

.rm-check {
  flex: 0 0 auto;
  border: none;
  background: transparent;
  padding: 0;
  margin-top: 2px;
  font-size: 1.7rem;
  line-height: 1;
  color: #8e8e93;
}

.rm-hero-main {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.rm-title-input {
  display: block;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-size: 22px;
  font-weight: 600;
  line-height: 1.3;
  padding: 0;
  margin: 0 0 6px;
  color: inherit;
  resize: none;
  overflow: hidden;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
  field-sizing: content;
  min-height: 1.3em;

  &.is-checked {
    text-decoration: line-through;
    color: #8e8e93;
  }

  &::placeholder {
    color: #c7c7cc;
    font-weight: 500;
  }
}

.rm-group {
  background: #fff;
  border-radius: 12px;
  margin-bottom: 18px;
  overflow: hidden;
}

.dark-theme .rm-group {
  background: #1c1c1e;
}

.rm-section-label {
  padding: 0 4px 8px 16px;
  font-size: 13px;
  color: #6c6c70;
}

.rm-group--actions {
  margin-bottom: 12px;
}

.rm-row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 48px;
  padding: 10px 14px;
  border: none;
  background: transparent;
  color: inherit;
  text-align: left;
  font-size: 17px;
  border-bottom: 0.5px solid rgba(60, 60, 67, 0.18);

  &:last-child {
    border-bottom: none;
  }

  &:disabled {
    opacity: 0.45;
  }
}

.dark-theme .rm-row {
  border-bottom-color: rgba(84, 84, 88, 0.55);
}

.rm-row-icon {
  flex: 0 0 auto;
  width: 22px;
  text-align: center;
  font-size: 1.15rem;
  color: #007aff;
}

.rm-row-label {
  flex: 0 0 auto;
  font-weight: 400;
}

.rm-row-value {
  flex: 1 1 auto;
  min-width: 0;
  text-align: right;
  color: #8e8e93;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-left: 8px;
}

.rm-row-chevron {
  flex: 0 0 auto;
  color: #c7c7cc;
  font-size: 0.95rem;
}

.rm-row--date {
  cursor: pointer;
}

.rm-row-value.is-placeholder {
  color: #c7c7cc;
}

/* 输入框隐藏；日历弹层 fixed 居中，不被列表滚动裁切 */
.rm-date-host {
  position: fixed;
  left: 0;
  right: 0;
  top: 22%;
  height: 0;
  z-index: 80;
  display: flex;
  justify-content: center;
  pointer-events: none;
}

.rm-date-host :deep(.v3dp__datepicker) {
  position: relative;
  pointer-events: auto;
}

.rm-date-host :deep(.v3dp__input_wrapper) {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
  opacity: 0;
  pointer-events: none;
}

.rm-date-host :deep(.v3dp__popout) {
  z-index: 81;
  left: 50%;
  transform: translateX(-50%);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.18);
}

.rm-row.is-open .rm-row-chevron {
  transform: rotate(90deg);
}

.rm-panel {
  padding: 4px 14px 14px;
  border-top: 1px solid rgba(60, 60, 67, 0.12);
  background: #fff;
}

.dark-theme .rm-panel {
  background: #1c1c1e;
  border-top-color: rgba(84, 84, 88, 0.45);
}

.rm-place-panel {
  display: flex;
  flex-direction: column;
  padding: 8px 14px 12px;
  gap: 6px;
  max-height: min(320px, 48vh);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

.rm-place-section {
  margin: 4px 2px 0;
  padding: 0 2px;
  font-size: 12px;
  font-weight: 560;
  color: #8e8e93;
}

.rm-place-option {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  border: none;
  border-radius: 10px;
  background: rgba(118, 118, 128, 0.08);
  color: inherit;
  text-align: left;
  padding: 11px 12px;
  font-size: 16px;
  line-height: 1.25;
  -webkit-tap-highlight-color: transparent;

  &:active {
    background: rgba(118, 118, 128, 0.16);
  }

  &.selected {
    background: rgba(0, 122, 255, 0.1);
  }
}

.dark-theme .rm-place-option {
  background: rgba(118, 118, 128, 0.18);

  &:active {
    background: rgba(118, 118, 128, 0.28);
  }

  &.selected {
    background: rgba(10, 132, 255, 0.22);
  }
}

.rm-place-leading {
  flex: 0 0 auto;
  width: 20px;
  text-align: center;
  color: #007aff;
  font-size: 1.05rem;
}

.rm-place-text {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.rm-place-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rm-place-option.selected .rm-place-name {
  color: #007aff;
  font-weight: 560;
}

.rm-place-sub {
  font-size: 12px;
  color: #8e8e93;
}

.rm-place-check {
  flex: 0 0 auto;
  color: #007aff;
  font-size: 1.15rem;
}

.rm-place-empty {
  margin: 0;
  padding: 12px 4px;
  color: #8e8e93;
  font-size: 14px;
  text-align: center;
}

.rm-row--action .rm-row-label,
.rm-row--danger .rm-row-label {
  flex: 1;
  text-align: center;
  color: #007aff;
}

.rm-row--danger .rm-row-label {
  color: #ff3b30;
}

.rm-switch {
  width: 51px;
  height: 31px;
  border-radius: 16px;
  background: #e9e9eb;
  position: relative;
  flex: 0 0 auto;
  transition: background 0.2s;

  &::after {
    content: "";
    position: absolute;
    top: 2px;
    left: 2px;
    width: 27px;
    height: 27px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    transition: transform 0.2s;
  }

  &.on {
    background: #34c759;
  }

  &.on::after {
    transform: translateX(20px);
  }

  &.disabled {
    opacity: 0.5;
  }
}

.rm-subtasks {
  list-style: none;
  margin: 0;
  padding: 0;
}

.rm-subtask {
  border-bottom: 0.5px solid rgba(60, 60, 67, 0.18);

  &:last-child {
    border-bottom: none;
  }
}

.rm-subtask-row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 46px;
  padding: 8px 14px;

  &.checked .rm-subtask-text {
    text-decoration: line-through;
    color: #8e8e93;
  }
}

.rm-subtask-check {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: #8e8e93;
  font-size: 1.35rem;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.rm-subtask-check .bi-check-circle {
  color: #007aff;
}

.rm-subtask-text {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  background: transparent;
  text-align: left;
  color: inherit;
  font-size: 17px;
  padding: 0;
  word-break: break-word;
}

.rm-subtask-del {
  border: none;
  background: transparent;
  color: #c7c7cc;
  font-size: 1.15rem;
  padding: 4px;
}

.rm-subtask-edit,
.rm-subtask-new-input {
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-size: 17px;
  padding: 12px 14px;
  color: inherit;
}

.rm-subtask--new {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-left: 14px;
}

.rm-subtask-add-ico {
  color: #8e8e93;
  font-size: 1.2rem;
}

.rm-scroll-pad {
  height: max(28px, env(safe-area-inset-bottom));
}

.cicle-icon {
  display: inline-block;
}

/* description 与标题左对齐；去掉 zoom/多余 padding 避免错位与裁切 */
.rm-hero-main :deep(.position-relative),
.rm-hero-main :deep(.todo-description),
.rm-hero-main :deep(.description-empty) {
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  zoom: normal !important;
}

.rm-hero-main :deep(.description-empty),
.rm-hero-main :deep(.todo-description) {
  font-size: 16px !important;
  line-height: 1.45 !important;
  color: #8e8e93;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
}

.rm-hero-main :deep(.todo-description) > *:first-child {
  margin-top: 0 !important;
}

.rm-hero-main :deep(.todo-description) > *:last-child {
  margin-bottom: 0 !important;
}

.rm-hero-main :deep(.todo-description) p {
  margin: 0 0 0.55em;
}

.rm-hero-main :deep(.todo-description) p:last-child {
  margin-bottom: 0;
}

.rm-hero-main :deep(.todo-description-textarea) {
  display: block;
  box-sizing: border-box;
  width: 100% !important;
  max-width: 100% !important;
  font-size: 16px !important;
  line-height: 1.45 !important;
  color: inherit;
  margin: 0 !important;
  min-height: 72px !important;
  border: none !important;
  border-radius: 0 !important;
  padding: 0 !important;
  background: transparent !important;
  overflow: hidden;
  resize: none;
}
</style>
